import { FIRE_FACILITY_OPTIONS } from "../constants";
import type { ProjectSummary } from "../types";
import { escapeHtml, renderPagePath } from "../view-helpers";

function renderFireFacilitySentence(project: ProjectSummary): string {
  if (!project.overview.fireFacilitySystems.length) {
    return "本工程设有________等消防设施。";
  }

  return `本工程设有${project.overview.fireFacilitySystems.join("、")}等消防设施。`;
}

export function renderProjectOverviewSections(project: ProjectSummary): string {
  return project.overview.buildingUnits
    .map(
      (unit, index) => `
        <article class="panel stack">
          <div class="inline-head">
            <h2>建筑单体 ${index + 1}</h2>
            ${
              project.overview.buildingUnits.length > 1
                ? `<button class="ghost-button small-button" type="button" data-remove-building-unit="${unit.id}">删除</button>`
                : ""
            }
          </div>
          <input type="hidden" name="buildingUnitId" value="${unit.id}" />
          <label class="field field-row">
            <span>建筑名称</span>
            <input name="buildingName" type="text" value="${escapeHtml(unit.buildingName)}" placeholder="填写建筑名称" />
          </label>
          <label class="field field-row">
            <span>结构类型</span>
            <input name="structureType" type="text" value="${escapeHtml(unit.structureType)}" placeholder="如框架、钢结构" />
          </label>
          <label class="field field-row">
            <span>使用性质</span>
            <input name="usageType" type="text" value="${escapeHtml(unit.usageType)}" placeholder="如办公、商业、住宅" />
          </label>
          <label class="field field-row">
            <span>耐火等级</span>
            <input name="fireResistanceLevel" type="text" value="${escapeHtml(unit.fireResistanceLevel)}" placeholder="填写耐火等级" />
          </label>
          <label class="field field-row">
            <span>地上层数</span>
            <input name="aboveGroundFloorCount" type="text" value="${escapeHtml(unit.aboveGroundFloorCount)}" placeholder="填写地上层数" />
          </label>
          <label class="field field-row">
            <span>地下层数</span>
            <input name="undergroundFloorCount" type="text" value="${escapeHtml(unit.undergroundFloorCount)}" placeholder="填写地下层数" />
          </label>
          <label class="field field-row">
            <span>建筑高度</span>
            <input name="buildingHeight" type="text" value="${escapeHtml(unit.buildingHeight)}" placeholder="填写建筑高度" />
          </label>
          <label class="field field-row">
            <span>地上建筑面积</span>
            <input name="aboveGroundBuildingArea" type="text" value="${escapeHtml(unit.aboveGroundBuildingArea)}" placeholder="填写地上建筑面积" />
          </label>
          <label class="field field-row">
            <span>地下建筑面积</span>
            <input name="undergroundBuildingArea" type="text" value="${escapeHtml(unit.undergroundBuildingArea)}" placeholder="填写地下建筑面积" />
          </label>
        </article>
      `,
    )
    .join("") +
    `
      <div class="panel stack">
        <p class="eyebrow">装修装饰</p>
        <label class="field">
          <span>装修部位</span>
          <input name="decorationPart" type="text" value="${escapeHtml(project.overview.decorationPart)}" placeholder="填写装修部位" />
        </label>
        <label class="field">
          <span>装修面积</span>
          <input name="decorationArea" type="text" value="${escapeHtml(project.overview.decorationArea)}" placeholder="填写装修面积" />
        </label>
        <label class="field">
          <span>装修所在层数</span>
          <input name="decorationFloors" type="text" value="${escapeHtml(project.overview.decorationFloors)}" placeholder="填写装修所在层数" />
        </label>
      </div>
      <div class="panel stack">
        <p class="eyebrow">建筑保温</p>
        <label class="field">
          <span>材料类别</span>
          <input name="insulationMaterialCategory" type="text" value="${escapeHtml(project.overview.insulationMaterialCategory)}" placeholder="填写材料类别" />
        </label>
        <label class="field">
          <span>保温所在层数</span>
          <input name="insulationFloors" type="text" value="${escapeHtml(project.overview.insulationFloors)}" placeholder="填写保温所在层数" />
        </label>
        <label class="field">
          <span>保温部位</span>
          <input name="insulationPart" type="text" value="${escapeHtml(project.overview.insulationPart)}" placeholder="填写保温部位" />
        </label>
        <label class="field">
          <span>保温材料</span>
          <input name="insulationMaterial" type="text" value="${escapeHtml(project.overview.insulationMaterial)}" placeholder="填写保温材料" />
        </label>
      </div>
      <div class="panel stack">
        <p class="eyebrow">消防设施（请用户自行勾选）</p>
        <p class="muted">这里由你直接勾选涉及的消防设施，后续按勾选结果生成报告中的对应内容。</p>
        <div class="checkbox-grid">
          ${FIRE_FACILITY_OPTIONS.map(
            (option) => `
              <label class="check-chip">
                <input type="checkbox" name="fireFacilitySystems" value="${option}" ${
                  project.overview.fireFacilitySystems.includes(option) ? "checked" : ""
                } />
                <span>${option}</span>
              </label>
            `,
          ).join("")}
        </div>
        <div class="generated-preview">
          <p class="generated-preview-label">生成预览</p>
          <p class="generated-preview-text">${escapeHtml(renderFireFacilitySentence(project))}</p>
        </div>
      </div>
    `;
}

export function renderProjectOverviewPage(project: ProjectSummary): string {
  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-basic-info">返回基本信息</button>
        <h1>项目概况</h1>
      </header>
      ${renderPagePath([
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "项目基本信息", href: `#/projects/basic-info?id=${project.id}` },
        { label: "项目概况" },
      ])}
      <form id="project-overview-form" class="stack">
        <div class="panel stack">
          <p class="muted">这里承接报告中“一、项目概况”的可变内容。单体信息、装修装饰和建筑保温都从这里维护。</p>
          <p class="mini-meta">“本工程设有 xxx 等消防设施”这一句在本页底部通过勾选消防设施生成，不需要另外手写。</p>
          <button class="ghost-button" type="button" data-action="add-building-unit">+ 增加建筑单体</button>
        </div>
        ${renderProjectOverviewSections(project)}
        <button class="primary-button" type="submit">保存项目概况</button>
      </form>
    </section>
  `;
}
